import { useCallback, useEffect, useState } from 'react';
import { request } from '@/lib/api.js';
import { LoadingState, PageHeader } from '@/components/ui/index.js';
import type { OrdersResponse } from '@/lib/types.js';
import { StripeSection } from './orders/StripeSection.js';
import { GrantsSection } from './orders/GrantsSection.js';
import { CnyOrdersSection } from './orders/CnyOrdersSection.js';
import { RedemptionsSection } from './orders/RedemptionsSection.js';
import { PromotionsSection } from './orders/PromotionsSection.js';

const PAGE_SIZE = 50;

/** The 5 sections that paginate independently. */
type SectionKey = 'cny' | 'redemptions' | 'stripe' | 'grants' | 'promotions';

const ZERO_OFFSETS: Record<SectionKey, number> = {
  cny: 0,
  redemptions: 0,
  stripe: 0,
  grants: 0,
  promotions: 0,
};

/**
 * Orders / 兑换 / 收入 — 5 separate sections so it's clear which streams
 * count as revenue (Stripe / CNY) vs which are stats only (兑换码 / promo)
 * vs which are admin-issued comps (grants). Each section lives in its
 * own file under `./orders/`.
 *
 * Each section paginates **independently**: they used to share one `offset`
 * with only 兑换码 having a pager, so every other section silently dropped
 * rows past page 1 (跨端审计 P1, 对不了账). Now each section owns its offset
 * and pager, and the API takes a per-section `<name>Offset` param.
 */
export function OrdersPage(): JSX.Element {
  const [data, setData] = useState<OrdersResponse | null>(null);
  const [offsets, setOffsets] = useState<Record<SectionKey, number>>(ZERO_OFFSETS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (offsetValues: Record<SectionKey, number>): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      for (const [key, value] of Object.entries(offsetValues)) {
        params.set(`${key}Offset`, String(value));
      }
      const res = await request<OrdersResponse>(`/admin/orders?${params.toString()}`);
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(offsets);
  }, [offsets, load]);

  const setSectionOffset = useCallback((key: SectionKey, next: number): void => {
    setOffsets((prev) => ({ ...prev, [key]: next }));
  }, []);

  return (
    <div>
      <PageHeader
        title="订单 / 兑换 / 收入"
        subtitle={
          <>
            Stripe 海外、国内订单、兑换码激活、promotion 使用
            <span className="text-slate-300 font-semibold mx-1">分开</span>
            统计。兑换码和 promo 都不是收入，只是信息。
          </>
        }
      />

      {error !== null && (
        <div className="text-sm text-rose-300 bg-rose-900/30 border border-rose-700/50 rounded-lg px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {loading && data === null ? (
        <LoadingState />
      ) : data !== null ? (
        <>
          <StripeSection
            data={data.stripe}
            offset={offsets.stripe}
            onOffsetChange={(next): void => setSectionOffset('stripe', next)}
            pageSize={PAGE_SIZE}
          />
          <GrantsSection
            data={data.grants}
            offset={offsets.grants}
            onOffsetChange={(next): void => setSectionOffset('grants', next)}
            pageSize={PAGE_SIZE}
          />
          <CnyOrdersSection
            data={data.real_revenue}
            offset={offsets.cny}
            onOffsetChange={(next): void => setSectionOffset('cny', next)}
            pageSize={PAGE_SIZE}
          />
          <RedemptionsSection
            data={data.redemptions}
            offset={offsets.redemptions}
            onOffsetChange={(next): void => setSectionOffset('redemptions', next)}
            pageSize={PAGE_SIZE}
            loading={loading}
          />
          <PromotionsSection
            data={data.promotions}
            offset={offsets.promotions}
            onOffsetChange={(next): void => setSectionOffset('promotions', next)}
            pageSize={PAGE_SIZE}
          />
        </>
      ) : null}
    </div>
  );
}
